import React from "react";
import * as Sentry from "@sentry/browser";
import styled from "styled-components/macro";

import ReportIcon from "ui/Icons/Report";
import NavLink from "ui/router/NavLink";
import { theme } from "ui/theme";
import Typography from "ui/Typography";

import {
  StudentNavMessageFragment,
  StudentNavReflectionFragment,
} from "../StudentNavigation.generated";

import { lineClamp } from "helpers/css";
import NoPII from "utils/NoPII";
import Avatar, { AvatarState } from "ui/eds/Avatar";
import { assertExists } from "helpers/assertExists";

const $StudentNavItem = styled(NavLink)`
  border-left: 0.25rem solid transparent;
  display: flex;
  align-items: self-start;
  padding: 0.5rem 0.75rem;
  position: relative;

  // Override the default NavLink styles.
  color: inherit;
  &:active,
  &:hover {
    background-color: ${theme.ColorBlurple50};
    color: inherit;
    text-decoration: none;
  }

  &.active {
    background-color: ${theme.ColorBlurple50};
    border-left-color: ${theme.ColorBlurple500};
  }
`;

const $ReportedAvatar = styled.div`
  background-color: ${theme.ColorSpace500};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--eds-size-4);
  width: var(--eds-size-4);
`;

const $Text = styled.div`
  flex: 1;
  margin-left: 1rem;
  overflow: hidden;
  word-break: break-word;
`;

const $PrimaryText = styled(Typography).attrs({
  size: "bodySerif",
})`
  ${lineClamp(2)}
`;

const $SecondaryText = styled(Typography).attrs({
  size: "bodySansSerif",
})`
  overflow: hidden;
  margin-bottom: 0.5rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${theme.FontSizeSubHeading};
`;

function hasNoStudentMessages(messages: StudentNavMessageFragment[]) {
  return !messages.some((m) => m.author.__typename === "Student");
}

function getResponse(messages: StudentNavMessageFragment[]) {
  if (hasNoStudentMessages(messages)) {
    return null;
  }

  const lastMessage = messages.at(-1);
  assertExists(lastMessage);
  // `message.content` can refer either to a text reflection or to the optional
  // note sent along with an audio or video reflection. Either way, display if
  // it's present in the message.
  if (lastMessage.content) {
    return `“${lastMessage.content}”`;
  }

  if (lastMessage.multipleChoiceOptionText) {
    return `“${lastMessage.multipleChoiceOptionText}”`;
  }

  // No text to display, indicate if a sticker was sent
  const stickersLength = lastMessage.stickers.length;
  if (stickersLength > 1) {
    return `${lastMessage.author.name} sent stickers`;
  } else if (stickersLength === 1) {
    return `${lastMessage.author.name} sent a sticker`;
  }

  // No text to display, indicate that it's a media reflection
  switch (lastMessage.media?.__typename) {
    case "AudioRecording":
      return "You replied with audio.";
    case "Video":
      return "You replied with a video.";
    default:
      // This should never happen, since a reflection either has media or is a
      // text reflection, but messages are typed such that both could be `null`.
      Sentry.captureMessage(
        "Reflection message contained neither media nor text",
      );
      return "You replied.";
  }
}

function getHeading(
  teacherName: string,
  messages: StudentNavMessageFragment[],
) {
  if (hasNoStudentMessages(messages)) {
    return `${teacherName} asked`;
  }

  const lastMessage = messages.at(-1);
  assertExists(lastMessage);

  return lastMessage.author.__typename === "Student"
    ? `You replied to ${teacherName}`
    : `${teacherName} replied`;
}

function getAnalytics({
  hasNotifications,
  isExpired,
  messages,
  session,
}: StudentNavReflectionFragment) {
  const analyticsProperties = {
    sessionId: session.id,
    sessionOwnerId: session.teacher.id,
  };

  if (messages.length) {
    const studentMessage = messages[0];
    return {
      analyticsName: "clicked_message",
      analyticsProperties: {
        ...analyticsProperties,
        extraMetadata: {
          hasAudio: studentMessage.media?.__typename === "AudioRecording",
          hasTeacherResponse: messages.length === 2,
          hasText: !!studentMessage.content,
          hasVideo: studentMessage.media?.__typename === "Video",
          isExpired,
          isUnread: hasNotifications,
          messageId: NoPII(studentMessage.id),
          textLength: studentMessage.content?.length || null,
        },
      },
    };
  }

  return {
    analyticsName: "clicked_active_session",
    analyticsProperties: {
      ...analyticsProperties,
      extraMetadata: {
        hasAudio: session.media?.__typename === "AudioRecording",
        hasText: !!session.description?.length,
        hasVideo: session.media?.__typename === "Video",
        textLength: session.description?.length || null,
      },
    },
  };
}

export interface StudentNavItemProps {
  reflection: StudentNavReflectionFragment;
}

function ReportedAvatar() {
  return (
    <$ReportedAvatar>
      <ReportIcon color="ColorWhite" role="img" title="Reported" />
    </$ReportedAvatar>
  );
}

export default function StudentNavItem({ reflection }: StudentNavItemProps) {
  const {
    studentReadAt,
    hasNotifications,
    hidden,
    messages,
    reportedByViewer,
    session,
  } = reflection;

  const isHidden = hidden || session.hidden;
  const isUnread = hasNotifications || !studentReadAt;

  let author = session.teacher;
  if (messages.length) {
    const lastMessage = messages[messages.length - 1];
    author = lastMessage.author;
  }

  if (isHidden) {
    return (
      <$StudentNavItem
        {...getAnalytics(reflection)}
        to={`/watch/${session.id}`}
      >
        <ReportedAvatar />
        <$Text>
          <$PrimaryText>This content was reported</$PrimaryText>
        </$Text>
      </$StudentNavItem>
    );
  }

  return (
    <$StudentNavItem {...getAnalytics(reflection)} to={`/watch/${session.id}`}>
      {reportedByViewer ? (
        <ReportedAvatar />
      ) : (
        <Avatar
          userId={author.id}
          name={author.name}
          profileImage={author.profileImage?.url}
          state={isUnread ? AvatarState.UNREAD : undefined}
        />
      )}
      <$Text>
        <$SecondaryText>
          {getHeading(session.teacher.name, messages)}
        </$SecondaryText>
        <$PrimaryText>{getResponse(messages) || session.prompt}</$PrimaryText>
      </$Text>
    </$StudentNavItem>
  );
}
